import React, { forwardRef } from "react";
import "./SaleInvoicePrint.css";

const UniversalInvoicePrint = forwardRef(({ invoice, type = "sale" }, ref) => {
  if (!invoice) return null;

  const user = invoice.user || invoice.additionalDetail || {};

  const invoiceTitleMap = {
    sale: "Sale Invoice",
    saleReturn: "Sale Return Invoice",
    purchase: "Purchase Invoice",
    purchaseReturn: "Purchase Return Invoice",
    dc: "Delivery Challan",
  };

  const taglineMap = {
    sale: "Point of Sale Invoice",
    saleReturn: "Sale Return Invoice",
    purchase: "Purchase Invoice",
    purchaseReturn: "Purchase Return Invoice",
    dc: "Delivery Challan",
  };

  const recipientLabel = type === "purchase" || type === "purchaseReturn" 
    ? "Vendor:" 
    : type === "dc" 
      ? "Deliver To:" 
      : "Bill To:";

  const calculateGstAmount = () => {
    if (!invoice.gstPercentage || invoice.gstPercentage === 0) return 0;
    
    const productTotal = (invoice.products || invoice.sales || []).reduce((sum, p) => {
      return sum + (parseFloat(p.calculatedTotal) || 0);
    }, 0);
    
    const discount = parseFloat(invoice.invoiceDiscount) || 0;
    const rent = parseFloat(invoice.invoiceRent) || 0;
    const subtotalBeforeGst = productTotal - discount + rent;
    const gstPercentage = parseFloat(invoice.gstPercentage) || 0;
    return (subtotalBeforeGst * gstPercentage) / 100;
  };

  const calculateSubtotalBeforeGst = () => {
    const productTotal = (invoice.products || invoice.sales || []).reduce((sum, p) => {
      return sum + (parseFloat(p.calculatedTotal) || 0);
    }, 0);
    const discount = parseFloat(invoice.invoiceDiscount) || 0;
    const rent = parseFloat(invoice.invoiceRent) || 0;
    return productTotal - discount + rent;
  };

  const gstAmount = calculateGstAmount();
  const subtotalBeforeGst = calculateSubtotalBeforeGst();
  const gstPercentage = invoice.gstPercentage || 0;

  const formatDisplayDate = (dateString) => {
    if (!dateString || dateString === "null") return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getInvoiceDate = () => {
    if (type === 'sale' || type === 'saleReturn') {
      if (invoice.sales && invoice.sales.length > 0) {
        const firstSale = invoice.sales[0];
        if (firstSale.saleEntryTime && firstSale.saleEntryTime !== "null") return firstSale.saleEntryTime;
        if (firstSale.recordUpdatedTime && firstSale.recordUpdatedTime !== "null") return firstSale.recordUpdatedTime;
        if (firstSale.returnTime && firstSale.returnTime !== "null") return firstSale.returnTime;
      }
    }
    
    if (type === 'purchase' || type === 'purchaseReturn') {
      if (invoice.products && invoice.products.length > 0) {
        const firstProduct = invoice.products[0];
        if (firstProduct.productEntryTime && firstProduct.productEntryTime !== "null") return firstProduct.productEntryTime;
        if (firstProduct.recordUpdatedTime && firstProduct.recordUpdatedTime !== "null") return firstProduct.recordUpdatedTime;
        if (firstProduct.returnTime && firstProduct.returnTime !== "null") return firstProduct.returnTime;
      }
    }
    
    return invoice.invoiceDate || invoice.date || null;
  };

  const invoiceDate = getInvoiceDate();
  const displayDate = invoiceDate ? formatDisplayDate(invoiceDate) : "";

  const getProductArray = () => {
    if (type === 'sale' || type === 'saleReturn') {
      return invoice.sales || invoice.products || [];
    }
    if (type === 'dc') {
      return invoice.products || invoice.deliveryChalanDtos || [];
    }
    return invoice.products || invoice.sales || [];
  };

  const products = getProductArray();
  const isDC = type === 'dc';

  const getQuantity = (product) => {
    if (type === 'saleReturn' || type === 'purchaseReturn') {
      return product.returnedQuantity || product.quantity || 0;
    }
    return product.quantity || product.returnedQuantity || 0;
  };

  const formatDetails = (product) => {
    const category = product.category ? product.category.toLowerCase() : "";
    
    if (category === "packet") {
      const parts = [];
      if (product.size) parts.push("S:" + product.size);
      if (product.ktae) parts.push("K:" + product.ktae);
      if (product.gram) parts.push("G:" + product.gram);
      return parts.length > 0 ? parts.join(" ") : "-";
    }
    
    if (category === "roll") {
      return product.size ? "Size:" + product.size : "-";
    }
    
    if (category === "reel" || category === "tota") {
      if (product.length) {
        return "Size:" + product.length;
      }
      return "-";
    }
    
    if (category === "box") {
      const parts = [];
      const boxSize = product.boxSize || product.size || "";
      if (boxSize) parts.push(boxSize);
      const particulars = product.particulars || product.particular || "";
      if (particulars) parts.push("(" + particulars + ")");
      return parts.length > 0 ? parts.join(" ") : "-";
    }
    
    const particular = product.particular || product.particulars || "";
    return particular || "-";
  };

  const productTotal = products.reduce((sum, p) => sum + (parseFloat(p.calculatedTotal || p.totalPrice || 0)), 0);

  const hasDriverInfo = invoice.driverName || invoice.vehicleNumber;

  const SingleInvoice = ({ copyType = "customer" }) => (
    <div className="invoice-single" style={{
  flex: 1,
  overflow: 'hidden',
padding: "16px 22px", 
 borderBottom: copyType === "customer" ? "3px dashed #999" : "none",
  pageBreakAfter: "avoid",
  pageBreakInside: "avoid",
  fontSize: "9px",
  boxSizing: 'border-box',
}}>
      {/* REMOVED "Sale Invoice" title */}

      <div className="invoice-header" style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "2px",
        flexWrap: "wrap"
      }}>
        <div className="logo-box">
          <div className="brand-logo">
            <div className="brand-logo-wrapper" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div className="monogram-logo" style={{ 
                width: "35px", 
                height: "35px", 
                borderRadius: "50%", 
                backgroundColor: "#1976d2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px"
              }}>
                {user.username ? user.username.charAt(0) : ""}
                {user.username ? user.username.charAt(1) : ""}
              </div>
              <div className="brand-text">
                <h2 className="brand-full-name" style={{ fontSize: "13px", margin: "0" }}>{user.username || ""}</h2>
                <p className="brand-tagline" style={{ fontSize: "8px", margin: "0", color: "#666" }}>{taglineMap[type] || "Invoice"}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="invoice-meta" style={{ fontSize: "9px", textAlign: "right" }}>
          <p style={{ margin: "1px 0" }}><strong>Invoice #:</strong> {invoice.invoiceNumber}</p>
          <p style={{ margin: "1px 0" }}><strong>Date:</strong> {displayDate}</p>
          {isDC && <p style={{ margin: "1px 0" }}><strong>DC #:</strong> DC-{invoice.invoiceNumber}</p>}
        </div>
      </div>

      <hr style={{ margin: "2px 0" }} />

      <div className="customer-section" style={{ marginBottom: "2px" }}>
        <h3 style={{ 
          fontSize: "14px", 
          margin: "2px 0 1px 0",
          fontWeight: "bold"
        }}>
          {recipientLabel}
        </h3>
        <p style={{ 
          margin: "1px 0", 
          fontSize: "14px",
          fontWeight: "500"
        }}>
          {invoice.customerName || invoice.vendorName || "Walk-in"}
        </p>
        
        {hasDriverInfo && (
          <div style={{ 
            marginTop: "2px", 
            padding: "2px 8px", 
            backgroundColor: "#e8f5e9", 
            borderRadius: "3px",
            border: "1px solid #a5d6a7",
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            fontSize: "12px"
          }}>
            {invoice.driverName && (
              <span><strong>👨‍💼 Driver:</strong> {invoice.driverName}</span>
            )}
            {invoice.vehicleNumber && (
              <span><strong>🚛 Vehicle #:</strong> {invoice.vehicleNumber}</span>
            )}
          </div>
        )}
        
        {invoice.description && <p style={{ fontSize: "9px", margin: "1px 0" }}><strong>Description:</strong> {invoice.description}</p>}
      </div>

      <table className={"invoice-table " + (!isDC ? "with-rate" : "without-rate")} style={{ 
        fontSize: "8px",
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "2px"
      }}>
        <thead>
          <tr style={{ backgroundColor: "#f0f0f0" }}>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "center", width: "5%" }}>#</th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "left", width: "20%" }}>Product</th>
             <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "left", width: "25%" }}>Details</th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "left", width: "12%" }}>Category</th>
            <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "center", width: "8%" }}>Qty</th>
            {!isDC && <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "right", width: "10%" }}>Rate</th>}
            <th style={{ border: "1px solid #ddd", padding: "2px 4px", textAlign: "right", width: "15%" }}>{isDC ? "Status" : "Total"}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => {
            const quantity = getQuantity(p);
            const price = parseFloat(p.price).toFixed(2);
            const total = parseFloat(p.calculatedTotal || p.totalPrice || 0).toFixed(2);
            const details = formatDetails(p);
            const isEven = i % 2 === 0;
            
            return (
              <tr key={i} style={{ backgroundColor: isEven ? "#fff" : "#f9f9f9" }}>
                <td style={{ border: "1px solid #ddd", padding: "1px 4px", textAlign: "center" }}>{i + 1}</td>
                <td style={{ border: "1px solid #ddd", padding: "1px 4px" }}>{p.productName}</td>
                <td className="details-cell" style={{ border: "1px solid #ddd", padding: "1px 4px", fontSize: "7.5px" }}>{details}</td>
                <td style={{ border: "1px solid #ddd", padding: "1px 4px" }}>{p.category}</td>
                <td style={{ border: "1px solid #ddd", padding: "1px 4px", textAlign: "center" }}>{quantity}</td>
                {!isDC && <td style={{ border: "1px solid #ddd", padding: "1px 4px", textAlign: "right" }}>{price}</td>}
                <td style={{ border: "1px solid #ddd", padding: "1px 4px", textAlign: "right" }}>
                  {isDC ? (
                    <span style={{ color: "#28a745", fontWeight: "bold" }}>✓</span>
                  ) : (
                    total
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!isDC && (
        <>
          <div className="summary" style={{
            fontSize: "14px",          // <--- CHANGED: Increased from 10.5px to 12px
            marginTop: "6px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: "8px",                // <--- CHANGED: Reduced slightly from 12px to 8px to save width
            width: "100%",
            paddingBottom: "5px"
          }}>
            {/* Each item is now a flex-row to keep labels and values together horizontally */}
            <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
              <span style={{ fontWeight: "500" }}>Subtotal:</span>
              <span>{productTotal.toFixed(2)}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
              <span style={{ fontWeight: "500" }}>Discount:</span>
              <span>{invoice.invoiceDiscount || 0}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
              <span style={{ fontWeight: "500" }}>Rent:</span>
              <span>{invoice.invoiceRent || 0}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
              <span style={{ fontWeight: "500" }}>Adjustments:</span>
              <span>{subtotalBeforeGst.toFixed(2)}</span>
            </div>

            {gstPercentage > 0 && (
              <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
                <span style={{ fontWeight: "500" }}>GST ({gstPercentage}%):</span>
                <span>{gstAmount.toFixed(2)}</span>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
              <span style={{ fontWeight: "500" }}>Amount Paid:</span>
              <span>{invoice.payBill || invoice.amountPaid || 0}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: "2px", borderLeft: "1px solid #000", paddingLeft: "10px" }}>
              <span style={{ fontWeight: "bold" }}>Amount Payable:</span>
              <span style={{ fontWeight: "bold" }}>{invoice.grandTotal}</span>
            </div>
          </div>
        </>
      )}

      {(type === "dc" || type === "sale" || type === "saleReturn") && (
        <div className="signature-footer" style={{ 
          marginTop: "5px", 
          padding: "5px", 
          borderTop: "2px dashed #ccc",
          display: "flex",
          justifyContent: "space-between",
          fontSize: "8px"
        }}>
          <div>
            <p style={{ margin: "0" }}><strong>{type === "dc" ? "Receiver's Signature:" : "Customer Signature:"}</strong></p>
            <p style={{ marginTop: "8px" }}>_________________________</p>
          </div>
          <div>
            <p style={{ margin: "0" }}><strong>Authorized Signature:</strong></p>
            <p style={{ marginTop: "8px" }}>_________________________</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
  <>
    {/* Add this style block to remove default print margins */}
    <style>{`
      @media print {
        @page { margin: 0; }
        body { margin: 0; padding: 0; }
        .invoice-container { 
          height: 100vh !important; 
          width: 100% !important; 
        }
        .invoice-single { 
          page-break-after: avoid; 
          page-break-inside: avoid; 
        }
      }
    `}</style>

    <div ref={ref} className="invoice-container" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',          // Exactly one full page height
      width: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#fff',
      overflow: 'hidden',       // Crucial: stops content from spilling to page 2
    }}>
      <SingleInvoice copyType="customer" />
      <SingleInvoice copyType="office" />
    </div>
  </>
);
});

export default UniversalInvoicePrint;